import { Component, Inject, OnInit, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  CompanyLocationConfigEx,
  CreatePayrollArgs,
  ManualCheck,
  MasterPayrollObject,
  PayrollConfig,
  PayrollConfigSetupData,
  PayrollStartConfig,
  PayrollStartData,
  PayrollStatuses,
  PayTypes,
  Schedule,
  TaxHold,
  TimeclockModel
} from '../../../models/payroll';
import { AuthService } from '../../../services/auth.service';
import { PayrollService } from '../../../services/payroll.service';
import { SharedService } from '../../../services/shared.service';
import { PayrollStartScheduleComponent } from './payroll-start-schedule/payroll-start-schedule.component';
import { PayrollManagement } from '../../../tools/payroll-management';
import { AclService } from '../../../services/acl.service';
import { AreDatesEqual, GetTimeInEST } from '../../../utils/dateUtils';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { PTOConfig } from '../../../models/paid-time-off';
import { SchoolDistrict } from '../../../models/tax-config';
import { ClientSetup } from '../../../models/client';
import { ErrorDetail, WarningLevel } from '../../../models/error-detail';
import { AdminService } from '../../../services/admin.service';
import { AuditService } from '../../../services/audit.service';
import { CompaniesService } from '../../../services/companies.service';
import { BusinessDaysService } from '../../../services/business-days.service';
import { parseDatePickerValue } from '../../../utils/datepicker-value-parser';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { DatePipe } from '@angular/common';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DistinctBy } from '../../../utils/math';
import { AppConfigService } from '../../../services/app-config.service';
import { IPayrollEventData } from '../../../models/audit-action';
import { ChangeRequestService } from '../../../services/change-request.service';
import { AERequestStatusType } from '../../../models/change-requests';
import { Store } from '@ngxs/store';
import {
  ClientSelectionLoadOpenChangeRequests,
  ClientSelectionState
} from '../../../state/ClientSelectionState/ClientSelection.state';
import { UserPreferencesService } from '../../../services/user-preferences';
import { UserPreferences } from '../../../models/user';
import { BaseComponent } from '../../../components/base/base.component';

@Component({
  selector: 'lib-payroll-start',
  templateUrl: './payroll-start.component.html',
  // styleUrls: ['./payroll-start.component.scss']
  standalone: false
})
export class PayrollStartComponent extends BaseComponent implements OnInit {
  @ViewChild('scheduleSelectModal', { static: true }) public scheduleSelectModal: TemplateRef<any>;

  @ViewChild(PayrollStartScheduleComponent) regStart: PayrollStartScheduleComponent;

  public activePayroll: MasterPayrollObject;
  public clientCode: string;
  public currentUserName: string;

  public clientSetup: ClientSetup;
  public errorsAndWarnings: ErrorDetail[];
  public isCsr: boolean;
  public loaded: boolean = false;
  public location: CompanyLocationConfigEx;
  public locked: boolean = true;
  // NOTE: the term 'config' is getting a bit overloaded at this point
  // ----
  // hold earnings/deductions/etc
  public payrollConfig: PayrollConfig = new PayrollConfig();
  // all clients earnings, deductions, taxes
  public payrollConfigSetupData: PayrollConfigSetupData = new PayrollConfigSetupData();
  // errors/warnings/locked flag
  public payrollStartConfig: PayrollStartConfig;

  public selSchedules: Schedule[] = [];
  public schedules: Schedule[];

  public schedulePicker = {};

  public startData: PayrollStartData;
  public startPayrollForm: UntypedFormGroup;
  public stateLocalTaxes: Record<string, Record<string, Record<string, SchoolDistrict[]>>> = {};
  public timeclockFile: TimeclockModel;
  public manualChecks: ManualCheck[];
  public ptoConfigs: PTOConfig[] = [];
  public minimumPrefundDate: Date | null;
  public prefundOffset: number;
  public minimumAvailableDate: { month: number; year: number; day: number } | null = null;
  public enableMultipleSchedules: boolean;
  public payDate: Date;
  public PayType: PayTypes;

  get scheduleIsValid() {
    // FIXME - Validate that this is this correct
    // We should only be checking form properties here, prefund logic is handled elsewhere
    if (this.activePayroll) {
      return true;
    }

    return this.regStart?.IsValid;
  }

  get lockedByProcessDueDate() {
    if (this.isCsr) {
      return false;
    }
    if (this.startData?.ActivePayroll && this.startData.ActivePayroll.PayType == this.PayType) {
      return false;
    }
    if (this.minimumPrefundDate == null) {
      return false;
    }
    const now = new Date();
    const minimumDate = this.minimumPrefundDate;
    const eod = this.env.endOfDayCutoff;
    const minimumDateEod = GetTimeInEST(eod.hours, eod.minutes, minimumDate);
    if (
      AreDatesEqual(now, minimumDate) &&
      (this.prefundOffset === 0 || this.prefundOffset == null)
    ) {
      return false;
    }

    return now >= minimumDateEod;
  }

  get hasSchedulePicks() {
    return Object.keys(this.schedulePicker).some(x => this.schedulePicker[x]);
  }

  constructor(
    private aclService: AclService,
    private adminService: AdminService,
    private appConfigService: AppConfigService,
    private auditService: AuditService,
    private authService: AuthService,
    private companyService: CompaniesService,
    private formBuilder: UntypedFormBuilder,
    private payrollService: PayrollService,
    private payrollMgmt: PayrollManagement,
    private route: ActivatedRoute,
    private router: Router,
    private sharedService: SharedService,
    public viewContainerRef: ViewContainerRef,
    private businessDayService: BusinessDaysService,
    private ngbModalService: NgbModal,
    private store: Store,
    private changeRequestService: ChangeRequestService,
    private userPrefSvc: UserPreferencesService,
    @Inject('env') private env: any
  ) {
    super();
  }

  ngOnInit(): void {
    // task 15267
    this.clientCode = this.route.snapshot.paramMap.get('client_code');
    this.aclService.CurrentUserHasMinimumRole('CSR').subscribe(result => {
      this.isCsr = result;
    });

    this.clientCode = this.route.snapshot.paramMap.get('client_code');
    this.currentUserName = this.authService.getCurrentUserName();

    this.enableMultipleSchedules = this.appConfigService.getConfig().EnableMultiSchedulePayroll;

    // check to make sure this is a valid payroll request
    this.companyService.getLocations(this.clientCode).subscribe(locationConfigs => {
      // if client is setup to auto pay - bounce them there
      const queryMap = this.route.snapshot.queryParamMap;
      const offCycle = queryMap.has('payType') && queryMap.get('payType') == '3';

      if (!offCycle && this.payrollMgmt.isClientSetupForAutoPayroll(locationConfigs)) {
        // US 25441 - allow regular payroll start when auto payroll has been skipped
        const location = locationConfigs[0];
        this.payrollMgmt
          .isAutoPayrollSkipped(this.clientCode, location.Id, location.Calendars[0].CalendarId)
          .subscribe(skipped => {
            if (skipped) {
              // automatic payroll has been skipped, allow client to continue with this regular payroll
              this.companyService.getClientSetup(this.clientCode).subscribe(resp => {
                this.clientSetup = resp;
                this._getStartData();
              });
            } else {
              // automatic payroll has not been skipped, redirect to auto payroll start
              this.router.navigate([`/client/${this.clientCode}/autopayroll`]);
            }
          });
      } else {
        this.companyService.getClientSetup(this.clientCode).subscribe(resp => {
          this.clientSetup = resp;
          this._getStartData();
        });
      }
    });

    // FIXME: change this to use payrollService.getPayrollConfigSetupData
    this._fetchPayrollConfigSetupData();

    this.companyService.getPTOConfigs(this.clientCode).subscribe(resp => {
      this.ptoConfigs = resp.filter(p => p.IsActive);
    });

    this.startPayrollForm = this.formBuilder.group({
      schedules: new UntypedFormGroup({})
    });
  }

  public formatDate(date: Date) {
    const datePipe = new DatePipe('en-Us');
    return datePipe.transform(date, 'MM/dd/yyyy');
  }

  public getPrefundWarnings(payDate: Date, onload: boolean = false) {
    // first step is to remove any 'PrefundDays' errors or warnings
    // previously stitched into the array
    this.errorsAndWarnings = this.errorsAndWarnings?.filter(x => x.FieldName != 'PrefundDays');
    if (payDate) {
      // task 12312
      // if a valid paydate is set, check prefund warnings

      var calendars = this.PayType == PayTypes.Regular ? this.selSchedules : this.schedules;

      for (const _schedule of calendars) {
        const payDateInfo = this.location.NextPayDates.find(
          x => x.CalendarId == _schedule.CalendarId
        );
        payDateInfo.TargetPayDate = payDate;

        // NOTE: each time the user changes the pay date,
        // the prefund checks are run. if any warnings are returned,
        // they are stiched into the errorsAndWarnings collection
        // which controls the disabled state of the start payroll button.
        this.payrollService
          .getPrefundWarnings(payDateInfo, [_schedule.CalendarId], this.PayType)
          .subscribe(warnings => {
            if (warnings && warnings.length) {
              // stitch any warnings into the 'before you begin' area
              for (let warning of warnings) {
                this.errorsAndWarnings.unshift(
                  new ErrorDetail('PrefundDays', warning, WarningLevel.Warning)
                );
              }
            }

            // Distinct the array
            this.errorsAndWarnings = DistinctBy(this.errorsAndWarnings, e => e.Message);

            // set the enabled/disabled state of the start payroll button
            this._setStartConfig(this.errorsAndWarnings);

            if (onload) {
              this.loaded = true;
            }
          });
      }
    } else if (onload) {
      // property that attempts to prevent UI position flicker on initial page load
      this.loaded = true;
    }
  }

  public renderTodaysDate() {
    return new Date().toLocaleDateString();
  }

  public onScheduleChange(calendarId: number | string) {
    // if multiple schedule feature flag is off, restrict to a single selection
    if (!this.enableMultipleSchedules) {
      this.schedulePicker = {};
      this.schedulePicker[calendarId] = true;
      return;
    }

    if (calendarId == 'selectAll') {
      for (let schedule of this.schedules) {
        this.schedulePicker[schedule.CalendarId] = this.schedulePicker['selectAll'];
      }
    } else {
      // if all schedules are selected, check 'select all' box
      // else, uncheck 'select all' box
      const selected = [];
      for (let key of Object.keys(this.schedulePicker)) {
        if (this.schedulePicker[key] && key != 'selectAll') {
          selected.push(key);
        }
      }

      this.schedulePicker['selectAll'] = Object.keys(this.schedules).length == selected.length;
    }
  }

  public onPayDateChanged(payDate: Date) {
    this._getErrorsAndWarnings(payDate, false);
  }

  public selectSchedules() {
    // loop through all 'true' entries in schedulePicker
    // to grab the calendarIds. then assign them to selSchedules
    let i = 0;
    for (let key of Object.keys(this.schedulePicker)) {
      if (this.schedulePicker[key]) {
        if (key == 'selectAll') {
          continue;
        }
        const cal = this.schedules.find(x => x.CalendarId == +key);

        if (!cal) {
          // this really shouldn't happen
          continue;
        }
        const pay = this.location.NextPayDates.find(x => x.CalendarId == cal.CalendarId);

        this.selSchedules.push(
          new Schedule(
            cal.CalendarId,
            i,
            new Date(pay.PeriodEnd), // FIXME - We are getting a string date value from API and this is where I decided to change it...
            pay.Frequency,
            pay.CalendarName,
            pay.PeriodBegin,
            pay.PeriodEnd,
            pay.TargetPayDate
          )
        );
        i++;
      }
    }

    this.selSchedules.sort((a, b) => {
      if (a.CalendarId < b.CalendarId) return 1;
      if (a.CalendarId > b.CalendarId) return -1;
      return 0;
    });

    this._getErrorsAndWarnings();
  }

  // #region public methods
  public startPayroll() {
    this._startNavigation();
  }

  public savePayrollConfig(config: PayrollConfig) {
    // Don't use normal assignment, we want the hasOverrides function from PayrollConfig
    // and the modal returns a prototype without the base functions
    Object.assign(this.payrollConfig, config);

    // when a column is held on payroll start, it will be hidden when the paygrid loads
    // clear any visibility preferences the user has set on this column.
    // if the user goes into the payroll after setting this hold and wants to see the column
    // again, that preference will remain after that selection
    this.userPrefSvc.getUserPreferences().subscribe((prefs: UserPreferences) => {
      const visibility = this.userPrefSvc.getPayrollColumnVisibility(this.clientCode, prefs);

      if (!visibility) {
        return;
      }

      const heldColumns = [];

      // loop through each held earning and deduction
      // and remove entry from visibility user preferences
      config.HoldEarnings.forEach(x => {
        const earning = this.payrollConfigSetupData.Earnings.find(e => e.Id == x);

        if (!earning) {
          return;
        }

        heldColumns.push(earning.Name);
      });

      config.HoldDeductions.forEach(x => {
        const deduction = this.payrollConfigSetupData.Deductions.find(d => d.Id == x);

        if (!deduction) {
          return;
        }

        heldColumns.push(deduction.Name);
      });

      for (let held of heldColumns) {
        // clear any preferences associated with held columns
        const rateColPref = visibility.find(x => x.Key == `${held}Rate`);
        if (rateColPref) {
          visibility.splice(visibility.indexOf(rateColPref), 1);
        }

        const hourColPref = visibility.find(x => x.Key == `${held}Hours`);
        if (hourColPref) {
          visibility.splice(visibility.indexOf(hourColPref), 1);
        }
      }

      this.userPrefSvc.setPayrollColumnVisibility(this.clientCode, visibility).subscribe();
    });
  }

  public markTouched() {
    this.startPayrollForm.markAllAsTouched();
  }

  // #endregion

  // #region private methods

  private _auditPayrollStart(seqNo: number = null) {
    const payroll = this.activePayroll;

    // audit the start/resume payroll button click event
    const auditAction = !payroll ? `start-payroll-clicked` : `resume-payroll-clicked`;

    let calendars = this.selSchedules;

    if (this.PayType == PayTypes.OffCycle) {
      calendars = this.schedules.map((s: Schedule) => {
        return { ...s, PeriodEnd: this.selSchedules[0].PeriodEnd };
      });
    }

    const masterAuditData: IPayrollEventData = {
      ClientCode: this.clientCode,
      SequenceNumber: seqNo || payroll?.SequenceNumber,
      Calendars: calendars,
      PayDate: payroll ? payroll.PayDate : parseDatePickerValue(this.regStart.f.PayDate.value),
      PayrollType: this.PayType,
      LocationId: 0 //FIXME multi location
    };

    const payrollStartData = {
      PayrollErrors:
        this.payrollStartConfig && this.payrollStartConfig.Errors
          ? this.payrollStartConfig.Errors.join(',')
          : '',
      PayrollWarnings:
        this.payrollStartConfig && this.payrollStartConfig.Warnings
          ? this.payrollStartConfig.Warnings.join(',')
          : '',
      PayrollLabel: this.regStart?.f?.Label?.value ?? '',
      ...masterAuditData
    };

    this.auditService.auditPayroll(auditAction, payrollStartData);

    // task 12962 - timeclock auditing
    if (this.clientSetup.HasTimeclock && this.timeclockFile) {
      const timeClockUsedData = {
        // we use period end off the timeclock file
        // it potentially may be different from the user entered PE
        PeriodEnd: this.timeclockFile.PeriodEndDate,
        ...masterAuditData
      };

      // company has time clock setup and user has selected a timeclock file
      this.auditService.auditPayroll('payroll-timeclock-file-used', timeClockUsedData);
    } else if (this.clientSetup.HasTimeclock && !this.timeclockFile) {
      const timeClockSkippedData = {
        PeriodEnd: this.selSchedules[0].PeriodEnd, // we use period end off the calendar //FIXME Multi schedule
        ...masterAuditData
      };

      // company has time clock setup but user has NOT selected a timeclock file
      this.auditService.auditPayroll('payroll-timeclock-file-skipped', timeClockSkippedData);
    }

    const requestedPayDate = parseDatePickerValue(this.regStart.f.PayDate.value);

    for (const selSchedule of this.selSchedules) {
      const originalPayDate = new Date(selSchedule.ScheduledPayDate);

      if (
        requestedPayDate.getMonth() != originalPayDate.getMonth() ||
        requestedPayDate.getDate() != originalPayDate.getDate() ||
        requestedPayDate.getFullYear() != originalPayDate.getFullYear()
      ) {
        // if user changed the pay date, capture it in an audit event
        this.auditService.auditPayroll('payroll-paydate-changed', masterAuditData, {
          CalendarId: selSchedule.CalendarId,
          OriginalPayDate: originalPayDate.toLocaleDateString()
        });

        // console.log([
        //   masterAuditData,
        //   {
        //     CalendarId: selSchedule.CalendarId,
        //     OriginalPayDate: originalPayDate.toLocaleDateString()
        //   }
        // ]);
      }
    }
  }

  // fetch all earnings, deductions, withholding taxes for the client
  // for the payroll config screen
  private _fetchPayrollConfigSetupData() {
    // FIXME: change this to use payrollService.getPayrollConfigSetupData
    forkJoin([
      this.companyService
        .getEarnings(this.clientCode)
        .pipe(map(data => (this.payrollConfigSetupData.Earnings = data))),
      this.companyService
        .getDeductions(this.clientCode)
        .pipe(map(data => (this.payrollConfigSetupData.Deductions = data))),
      this.companyService
        .getTaxConfigs(this.clientCode)
        .pipe(map(data => (this.payrollConfigSetupData.WithholdingTaxes = data)))
    ]).subscribe(() => {
      // for each active entry in WithholdingTaxes (excluding Federal Taxes)
      // grab a distinct list of the state abbreviations. for each state
      // check if it has any active local taxes associated with it
      const states = [
        ...new Set(
          this.payrollConfigSetupData.WithholdingTaxes.filter(
            x => x.StateAbbreviation != 'FED' && x.IsActive
          ).map(x => x.StateAbbreviation)
        )
      ];

      if (!states.length) {
        return;
      }

      for (let state of states) {
        this.adminService.getLocalTaxesByCounty(state).subscribe(locals => {
          this.stateLocalTaxes[state] = locals;
        });
      }
    });
  }

  private _generateDueDateWarnings(date: Date) {
    this.errorsAndWarnings = this.errorsAndWarnings?.filter(x => x.FieldName != 'DueDateWarnings');
    if (!this.authService.isInAnyRole(['CSR1', 'RunPayroll', 'Client Portal Admin'])) {
      return;
    }
    //   const date = parseDatePickerValue(this.f.PayDate?.value);

    if (!date) {
      return;
    }

    for (const schedule of this.selSchedules) {
      const payDateInfo = this.location.NextPayDates.find(x => x.CalendarId == schedule.CalendarId);

      if (!payDateInfo) {
        continue;
      }

      const offset = payDateInfo.RequiredAchPrefundDays * -1;
      this.prefundOffset = payDateInfo.RequiredAchPrefundDays;
      this.businessDayService.getBusinessDay(date, offset).subscribe(minimumPrefundDate => {
        this.minimumPrefundDate = minimumPrefundDate;

        const now = new Date();
        const eod = this.env.endOfDayCutoff;
        const minDate = GetTimeInEST(eod.hours, eod.minutes, minimumPrefundDate);
        //
        // FIXME this conditional should probably be removed due to above early out
        //
        if (this.activePayroll != null) {
          if (AreDatesEqual(minDate, now) && (this.prefundOffset == 0 || now < minDate)) {
            const eod = this.env.endOfDayCutoff;
            const endOfDay = GetTimeInEST(eod.hours, eod.minutes);
            const eodFormat = endOfDay.toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: 'numeric',
              timeZoneName: 'short',
              hour12: true
            });
            this.errorsAndWarnings.unshift(
              new ErrorDetail(
                'DueDateWarnings',
                `This payroll is due to be submitted by ${eodFormat} today in order for ConnectPay and your Bank to process your payroll in a timely manner.`,
                WarningLevel.Warning
              )
            );
          } else if (now < minDate) {
            //we have an active payroll, and are not in the prefund window.
            //Add warning message.
            this.errorsAndWarnings.unshift(
              new ErrorDetail(
                'DueDateWarnings',
                `This payroll is due to be submitted on ${
                  minDate.getMonth() + 1
                }/${minDate.getDate()}/${minDate.getFullYear()} in order for ConnectPay and your Bank to process your payroll in a timely manner.`,
                WarningLevel.Warning
              )
            );
          } else if (now > minDate) {
            this.errorsAndWarnings.unshift(
              new ErrorDetail(
                'DueDateWarnings',
                `This payroll is overdue. You can resume this payroll but will need to contact your Payroll Specialist to submit.`,
                WarningLevel.Warning
              )
            );
          }
        }
      });
    }
  }

  private _filterNullConfigValues(c: PayrollConfig) {
    c.HoldEarnings = c.HoldEarnings.filter(x => x != null);
    c.HoldDeductions = c.HoldDeductions.filter(x => x != null);
    c.HoldTaxes = c.HoldTaxes.filter(x => x.Id != null && x.Id != undefined);
    c.HoldTaxes.map(x =>
      !x.OverridePercentage
        ? (x.OverridePercentage = 0)
        : (x.OverridePercentage = x.OverridePercentage)
    );
    c.HoldTimeOff = c.HoldTimeOff.filter(x => x != null);
  }

  private _getQueryParams(params?: {}) {
    // if calendar_id query param exists, append it back to the query params
    // to the payroll start page
    if (this.route.snapshot.queryParamMap.has('calendar_id')) {
      params = {
        ...params,
        ...{ calendar_id: this.route.snapshot.queryParamMap.get('calendar_id') }
      };
    }

    if (this.route.snapshot.queryParamMap.has('payType')) {
      params = { ...params, ...{ payType: this.route.snapshot.queryParamMap.get('payType') } };
    }

    return params;
  }

  private _getStartData() {
    this.payrollService.getPayrollStartData(this.clientCode).subscribe(resp => {
      this.startData = resp;

      if (
        this.startData?.ActivePayroll &&
        [PayTypes.Regular, PayTypes.OffCycle].includes(this.startData.ActivePayroll.PayType)
      ) {
        this.activePayroll = this.startData.ActivePayroll;
      }

      this._setComponentDisplay();

      if (this.startData?.ActivePayroll != null) {
        const cachedPayroll = this.payrollMgmt.GetPayrollFromCache(
          this.clientCode,
          this.startData.ActivePayroll.Year,
          this.startData.ActivePayroll.SequenceNumber
        );

        if (
          cachedPayroll != null &&
          this.startData.ActivePayroll.LastEditBy != cachedPayroll.LastEditBy
        ) {
          this.payrollMgmt.clearCachedPayroll(
            this.clientCode,
            this.startData.ActivePayroll.Year,
            this.startData.ActivePayroll.SequenceNumber
          );
        }
      }

      this._setStartConfig();
      this._init();
    });
  }

  private _init() {
    this.location = this.startData.Locations[0];

    this.schedules = [];
    let i = 0;
    for (let calendar of this.location.Calendars) {
      const pay = this.location.NextPayDates.find(x => x.CalendarId == calendar.CalendarId);
      this.schedules.push(
        new Schedule(
          pay.CalendarId,
          i,
          pay.PeriodEnd,
          pay.Frequency,
          pay.CalendarName,
          pay.PeriodBegin,
          pay.PeriodEnd,
          pay.TargetPayDate
        )
      );
      i++;
    }

    this.schedules.sort((a, b) => {
      if (a.CalendarId < b.CalendarId) return 1;
      if (a.CalendarId > b.CalendarId) return -1;
      return 0;
    });

    if (this.activePayroll) {
      // Use Object.assign so that hasOverrides base function is available
      this.activePayroll.Config = Object.assign(new PayrollConfig(), this.activePayroll.Config);
      this.location = this.startData.Locations.find(x => x.Id == this.activePayroll.LocationId);
      this.selSchedules = this.activePayroll.Calendars;
    } else if (this.PayType == PayTypes.OffCycle) {
      // For off-cycle payrolls we will only be displaying a single entry for the user.
      // On submission, we take the period end date selected by them and apply it to all schedules for the client
      var calendarId = this.schedules[0].CalendarId;
      this.selSchedules = [
        new Schedule(calendarId, 0, null, 0, 'Multiple', new Date(), new Date(), null)
      ];
    } else if (this.location.Calendars.length == 1) {
      const cal = this.location.Calendars[0];
      const pay = this.location.NextPayDates.find(x => x.CalendarId == cal.CalendarId);

      // set the selected schedules here so the picker doesn't appear
      this.selSchedules.push(
        new Schedule(
          cal.CalendarId,
          0,
          new Date(pay.PeriodEnd),
          pay.Frequency,
          pay.CalendarName,
          new Date(pay.PeriodBegin),
          new Date(pay.PeriodEnd),
          new Date(pay.TargetPayDate)
        )
      );
    } else if (this.route.snapshot.queryParamMap.has('calendar_id')) {
      // preselect calendar id that came in query param (from a task)
      // but still take user to the screen to select schedules
      const calId = +this.route.snapshot.queryParamMap.get('calendar_id');
      this.schedulePicker[calId] = true;
    }

    // TASK 13012
    if (this.sharedService.timeclockSelection) {
      this.timeclockFile = this.sharedService.timeclockSelection.TimeClockFile;

      // NOTE: important that we clear the timeclock selection after we use it
      // shared service will retain it if the user naviates away
      this.sharedService.timeclockSelection = null;
    }

    // TASK 13575
    if (this.sharedService.manualChecks) {
      this.manualChecks = this.sharedService.manualChecks;

      // NOTE: important that we clear the manual checks after we use it
      // shared service will retain it if the user naviates away
      this.sharedService.manualChecks = null;
    }

    if (this.activePayroll) {
      this._getErrorsAndWarnings(this.activePayroll.PayDate, true);
    } else if (this.selSchedules && this.selSchedules.length == 1) {
      this._getErrorsAndWarnings(this.selSchedules[0].ScheduledPayDate, true);
    }
  }

  private _initializePayGrid() {
    if (!this.selSchedules?.length) {
      return;
    }
    // task 12070/4. pass the user selected values for
    // period end and pay date to the create payroll endpoint.

    let calendars = this.selSchedules;

    if (this.PayType == PayTypes.OffCycle) {
      calendars = this.schedules.map((s: Schedule) => {
        return { ...s, PeriodEnd: this.selSchedules[0].PeriodEnd };
      });
    }

    var pdi = this.location.NextPayDates.find(x => x.CalendarId == calendars[0].CalendarId);
    pdi.TargetPayDate = parseDatePickerValue(this.regStart.f.PayDate.value);

    const args = new CreatePayrollArgs(
      this.clientCode,
      pdi,
      // FIXME: ITER35 refactor to include all selected schedules
      calendars,
      this.PayType,
      this.payrollConfig,
      this.timeclockFile?.Id,
      this.manualChecks,
      this.regStart?.f?.Label?.value ?? ''
    );
    this.payrollMgmt.createPayroll(args).subscribe(payroll => {
      // bug #23128, capture sequence number in the start-payroll-clicked event
      this._auditPayrollStart(payroll.SequenceNumber);
      this.router.navigate([
        `/client/${this.clientCode}/payroll/${payroll.Year}/${payroll.SequenceNumber}`
      ]);
    });
  }

  // due to the way the config values are set in the Holds/Overrides screen
  // we need to filter out any null values set there before we create the payroll.
  // also need to parse out the single local tax checkbox into local tax codes
  private _parseConfigSettings(c: PayrollConfig) {
    this._filterNullConfigValues(c);

    const heldLocals = c.HoldTaxes.filter(x => x.Id.includes('Locals-'));
    heldLocals.forEach(heldLocal => {
      const state = heldLocal.Id.split('Locals-')[1];

      // get any locals associated w this state
      const locals = this.stateLocalTaxes[state];

      Object.keys(locals).forEach(key => {
        Object.keys(locals[key]).forEach(k => {
          locals[key][k].forEach(tax => {
            // congrats, you've arrived in the seventh layer of hell
            c.HoldTaxes.push(new TaxHold(tax.ResidentTaxCode));
          });
        });
      });
    });

    // remove the entries that were added for UI control only
    c.HoldTaxes = c.HoldTaxes.filter(x => !x.Id.includes('Locals-'));
  }

  private _removeColVizPref(name: string, val) {
    if (!name) {
      return;
    }

    const hours = `${name}Hours`;
    const rate = `${name}Rate`;

    if (val[this.clientCode].hasOwnProperty(hours)) {
      delete val[this.clientCode][hours];
    }

    if (val[this.clientCode].hasOwnProperty(rate)) {
      delete val[this.clientCode][rate];
    }
  }

  private _setComponentDisplay() {
    // look for query params to
    // determine if regular or off cycle was
    // just started from the landing page
    const queryMap = this.route.snapshot.queryParamMap;
    if (queryMap.has('payType') && queryMap.get('payType') == '3') {
      this.PayType = PayTypes.OffCycle;
    } else {
      this.PayType = PayTypes.Regular;
    }

    const activePayroll = this.startData.ActivePayroll;

    if (activePayroll != null && activePayroll.PayType != PayTypes.Manual) return;

    if (
      this.sharedService.manualChecks == null ||
      (this.sharedService.timeclockSelection == null && this.PayType == PayTypes.Regular)
    ) {
      // We have never visited the manual checks screen, likely because the user refreshed the page
      // Go back there so we can prompt user for any checks
      this.router.navigate([`/client/${this.clientCode}/manual-checks`], {
        queryParams: this._getQueryParams()
      });

      return;
    }
  }

  private _setStartConfig(errorDetail: ErrorDetail[] = null) {
    this.payrollStartConfig = this.payrollMgmt.buildPayrollConfig(
      this.clientSetup,
      this.startData?.ActivePayroll,
      errorDetail,
      this.PayType,
      this.startData
    );

    this.locked = this.payrollStartConfig.IsLocked;
  }

  private _startNavigation() {
    this._parseConfigSettings(this.payrollConfig);

    const payroll = this.activePayroll;
    if (!payroll) {
      this._initializePayGrid();
    } else if (payroll.Status === PayrollStatuses.Hold) {
      this._auditPayrollStart();
      // task 7975 any CSR is allowed to resume,
      // issue a status change to In Process
      this.payrollService
        .updatePayrollStatus(
          this.clientCode,
          payroll.Year,
          payroll.SequenceNumber,
          PayrollStatuses.Started
        )
        .subscribe(() => {
          this.router.navigate([
            `/client/${this.clientCode}/payroll/${payroll.Year}/${payroll.SequenceNumber}`
          ]);
        });
    } else if (payroll.Status === PayrollStatuses.Started) {
      if (this.startData?.HasPayrollLastEditorSessionExpired) {
        this.payrollService
          .updatePayrollOwner(this.clientCode, payroll.Year, payroll.SequenceNumber)
          .subscribe(() => {
            this._auditPayrollStart();
            this.router.navigate([
              `/client/${this.clientCode}/payroll/${payroll.Year}/${payroll.SequenceNumber}`
            ]);
          });
      } else {
        this._auditPayrollStart();
        this.router.navigate([
          `/client/${this.clientCode}/payroll/${payroll.Year}/${payroll.SequenceNumber}`
        ]);
      }
    }
  }

  private _getErrorsAndWarnings(payDate: Date = null, onload: boolean = false) {
    // fetch payroll errors and warnings from the API

    let getWarnings: Observable<ErrorDetail[]>[] = [];
    for (const schedule of this.selSchedules) {
      getWarnings.push(
        this.companyService.getPayrollErrorsAndWarnings(
          this.clientCode,
          `${this.location.Id}`,
          `${schedule.CalendarId}`
        )
      );
    }

    forkJoin(getWarnings).subscribe(warnings => {
      this.store.dispatch(new ClientSelectionLoadOpenChangeRequests()).subscribe(_ => {
        this.store.selectOnce(ClientSelectionState.getOpenChangeRequests).subscribe(requests => {
          if (requests.length > 0) {
            const errorArray: ErrorDetail[] = [
              {
                Message:
                  'At least one person in this payroll has a pending employee change request.',
                WarningLevel: WarningLevel.Warning,
                FieldName: 'OpenChangeRequest'
              }
            ];
            warnings.push(errorArray);
          }
          this._processErrorsAndWarnings(warnings, payDate, onload);
        });
      });
    });
  }

  private _processErrorsAndWarnings(
    warnings: ErrorDetail[][],
    payDate: Date = null,
    onload: boolean = false
  ) {
    this.errorsAndWarnings = warnings.flat();

    // Distinct the array
    this.errorsAndWarnings = DistinctBy(this.errorsAndWarnings, e => e.Message);

    const date = this.activePayroll ? this.activePayroll.PayDate : payDate;
    if (date) {
      this._generateDueDateWarnings(date);
      this.getPrefundWarnings(date, onload);
    } else {
      // set the enabled/disabled state of the start payroll button
      this._setStartConfig(this.errorsAndWarnings);
    }
  }

  // #endregion
}
